import { useEffect, useRef } from 'react';
import { canvasHeight, canvasWidth, groundSlope, groundYEnd, groundYStart } from './constants';

const backgroundGameSpeed = 0.1;
const backgroundSlope = groundSlope / 2;

// Based on the place-mountain-filled icon
const mountainBase64 =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjE2MC42MDI3IDE4Ni45ODM3IDIxLjExMSAxOC44OTEzIiB3aWR0aD0iMjEuMTExcHgiIGhlaWdodD0iMTguODkxM3B4Ij48cGF0aCBkPSJNIDE3My4xOTcgMTg4LjE0MiBDIDE3My43NjIgMTg2LjU2OSAxNzYuMDAxIDE4Ni42MDggMTc2LjUxMSAxODguMTk5IEwgMTgxLjY1MyAyMDQuMjQyIEMgMTgxLjkxMiAyMDUuMDQ5IDE4MS4zMSAyMDUuODc0IDE4MC40NjMgMjA1Ljg3NCBMIDE3MS4xNzIgMjA1Ljg3NCBMIDE3MS4xNTcgMjA1Ljg3NSBMIDE2MS44NTQgMjA1Ljg3NSBDIDE2MC45OTQgMjA1Ljg3NSAxNjAuMzg5IDIwNS4wMjUgMTYwLjY3NCAyMDQuMjExIEwgMTY0LjU2MiAxOTMuMTMgQyAxNjUuMDk3IDE5MS42MDcgMTY3LjIzMiAxOTEuNTU4IDE2Ny44MzcgMTkzLjA1NSBMIDE3Mi43MTYgMjA0Ljc5MiBMIDE2NS43NzUgMjA0LjM3NCBMIDE2Ny4zNjkgMjA0LjM3NCBMIDE2OS43MzkgMTk3Ljc3MSBMIDE2OS43MzkgMTk3Ljc3MyBMIDE3My4xOTcgMTg4LjE0MiBaIiBzdHlsZT0ic3Ryb2tlLXdpZHRoOiAxOyIgaWQ9Im9iamVjdC0wIiB0cmFuc2Zvcm09Im1hdHJpeCgxLCAwLCAwLCAxLCAtMS40MjEwODU0NzE1MjAyMDA0ZS0xNCwgLTIuODQyMTcwOTQzMDQwNDAxZS0xNCkiLz48L3N2Zz4=';
const mountainSize = canvasHeight * 0.85;
const mountainInitialX = canvasWidth;
const mountainInitialY = canvasHeight - mountainSize;
const mountainOpacity = 0.05;

const useBackgroundRef = (onComponentLoad: () => void) => {
  const img = useRef<HTMLImageElement>(null);

  useEffect(() => {
    img.current = new Image();
    img.current.src = mountainBase64;
    img.current.onload = () => {
      onComponentLoad();
    };
  }, [onComponentLoad]);

  const obstaclesRef = useRef({
    msCounter: 0,
    mountainXYs: [[mountainInitialX - mountainSize, mountainInitialY + mountainSize * backgroundSlope]] as [number, number][],
    draw(ctx: CanvasRenderingContext2D, msDelta: number) {
      if (!img.current) {
        return;
      }

      this.msCounter += msDelta;
      const xDelta = msDelta * backgroundGameSpeed;
      const yDelta = xDelta * backgroundSlope;

      // Add a new mountain once once overlap would not occur
      if (this.mountainXYs.length === 0 || this.mountainXYs[this.mountainXYs.length - 1][0] + mountainSize < mountainInitialX) {
        this.mountainXYs.push([mountainInitialX, mountainInitialY]);
      }

      this.mountainXYs.forEach(mountainXY => {
        mountainXY[0] -= xDelta;
        mountainXY[1] += yDelta;
      });
      this.mountainXYs = this.mountainXYs.filter(([mountainX]) => mountainX + mountainSize > 0);

      ctx.save();
      ctx.globalAlpha = mountainOpacity;
      for (let i = 0; i < this.mountainXYs.length; i++) {
        ctx.drawImage(img.current, this.mountainXYs[i][0], this.mountainXYs[i][1], mountainSize, mountainSize);
      }
      ctx.restore();

      // Draw the ground overtop of the mountains
      ctx.save();
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, groundYStart);
      ctx.lineTo(canvasWidth, groundYEnd);
      ctx.lineTo(canvasWidth, canvasHeight);
      ctx.lineTo(0, canvasHeight);
      ctx.closePath();
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.restore();

      ctx.save();
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(0, groundYStart);
      ctx.lineTo(canvasWidth, groundYEnd);
      ctx.stroke();
      ctx.restore();
    }
  });

  return obstaclesRef;
};

export default useBackgroundRef;
